<template>
  <div class="form-canvas" @dragover.prevent @drop="onDrop">
    <div v-if="fields.length === 0" class="empty-canvas">
      <div class="empty-icon">
        <i class="bx bx-plus-circle"></i>
      </div>
      <h5>Comece a construir seu formulário</h5>
      <p>Arraste campos da barra lateral ou clique em "+ Adicionar Campo"</p>
    </div>

    <draggable
      v-else
      v-model="localFields"
      :group="{ name: 'fields', pull: false, put: true }"
      handle=".drag-handle"
      animation="200"
      ghost-class="ghost-field"
      @end="onReorder"
    >
      <div
        v-for="field in localFields"
        :key="field.id || field._tempId"
        class="field-card"
        :class="{
          selected: isSelected(field),
          'has-conditions': hasConditions(field)
        }"
        @click="selectField(field)"
      >
        <!-- Drag Handle & Label Row -->
        <div class="field-header">
          <div class="drag-handle">
            <i class="bx bx-grid-vertical"></i>
          </div>
          <div class="field-label-row">
            <i
              v-if="isStandard(field)"
              class="bx bx-lock-alt text-warning mr-1"
              title="Campo padrão do sistema"
            ></i>
            <span class="field-label">{{ field.label || 'Sem label' }}</span>
            <span v-if="isStandard(field)" class="badge badge-warning"
              >Standard</span
            >
            <span v-if="field.required" class="badge badge-required"
              >Obrigatório</span
            >
            <span v-if="hasConditions(field)" class="badge badge-conditions"
              >Condicional</span
            >
          </div>
          <div class="field-actions">
            <button
              v-if="!isProtectedField(field)"
              type="button"
              class="btn-action"
              @click.stop="duplicateField(field)"
              title="Duplicar"
            >
              <i class="bx bx-copy"></i>
            </button>
            <button
              v-if="!isProtectedField(field)"
              type="button"
              class="btn-action btn-delete"
              @click.stop="removeField(field)"
              title="Remover"
            >
              <i class="bx bx-trash"></i>
            </button>
            <span
              v-if="isProtectedField(field)"
              class="protected-badge"
              title="Campo obrigatório do sistema"
            >
              <i class="bx bx-shield"></i>
            </span>
          </div>
        </div>

        <!-- Field Preview -->
        <div class="field-preview">
          <!-- Text Input Preview -->
          <template v-if="isTextType(field)">
            <input
              type="text"
              class="preview-input"
              :placeholder="field.placeholder || 'Digite...'"
              disabled
            />
          </template>

          <!-- Textarea Preview -->
          <template v-else-if="getFieldTypeName(field) === 'textarea'">
            <textarea
              class="preview-textarea"
              :placeholder="field.placeholder || 'Digite...'"
              rows="3"
              disabled
            ></textarea>
          </template>

          <!-- Select/Dropdown Preview -->
          <template v-else-if="isSelectType(field)">
            <div class="preview-select">
              <span>{{ field.placeholder || 'Selecione...' }}</span>
              <i class="bx bx-chevron-down"></i>
            </div>
            <div
              v-if="field.options && field.options.length > 0"
              class="preview-options"
            >
              <div
                v-for="(opt, idx) in field.options.slice(0, 4)"
                :key="idx"
                class="preview-option"
              >
                <span
                  class="option-bullet"
                  :style="{ backgroundColor: getOptionColor(idx) }"
                ></span>
                {{ opt.label || opt }}
              </div>
              <div
                v-if="field.options.length > 4"
                class="preview-option text-muted"
              >
                +{{ field.options.length - 4 }} mais opções
              </div>
            </div>
          </template>

          <!-- Radio Preview -->
          <template v-else-if="getFieldTypeName(field) === 'radio'">
            <div class="preview-radio-group">
              <div
                v-for="(opt, idx) in (field.options || []).slice(0, 4)"
                :key="idx"
                class="preview-radio"
              >
                <span class="radio-circle"></span>
                {{ opt.label || opt }}
              </div>
            </div>
          </template>

          <!-- Checkbox Preview -->
          <template
            v-else-if="
              getFieldTypeName(field) === 'checkbox' ||
              getFieldTypeName(field) === 'terms'
            "
          >
            <div class="preview-checkbox">
              <span class="checkbox-box"></span>
              <span>{{
                field.options && field.options[0]
                  ? field.options[0].label
                  : field.label
              }}</span>
            </div>
          </template>

          <!-- Date Preview -->
          <template v-else-if="isDateType(field)">
            <div class="preview-date">
              <i class="bx bx-calendar"></i>
              <span>{{ field.placeholder || 'Selecione a data...' }}</span>
            </div>
          </template>

          <!-- File Upload Preview -->
          <template
            v-else-if="
              getFieldTypeName(field) === 'file' ||
              getFieldTypeName(field) === 'image'
            "
          >
            <div class="preview-file">
              <i class="bx bx-cloud-upload"></i>
              <span>Arraste ou clique para enviar</span>
            </div>
          </template>

          <!-- Heading Preview -->
          <template v-else-if="getFieldTypeName(field) === 'heading'">
            <h4 class="preview-heading">
              {{ field.default_value || field.label }}
            </h4>
          </template>

          <!-- Divider Preview -->
          <template v-else-if="getFieldTypeName(field) === 'divider'">
            <hr class="preview-divider" />
          </template>

          <!-- Default Preview -->
          <template v-else>
            <input
              type="text"
              class="preview-input"
              :placeholder="field.placeholder || ''"
              disabled
            />
          </template>
        </div>

        <!-- Help Text -->
        <div v-if="field.help_text" class="field-help">
          {{ field.help_text }}
        </div>

        <!-- Conditional Fields Indicator -->
        <div v-if="hasConditions(field)" class="conditions-indicator">
          <i class="bx bx-git-branch"></i>
          <span>Exibir se: {{ getConditionSummary(field) }}</span>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FormCanvas',
  components: {
    draggable
  },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    selectedField: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      localFields: [],
      optionColors: ['#4285f4', '#34a853', '#fbbc04', '#ea4335', '#9c27b0', '#00bcd4']
    }
  },
  watch: {
    fields: {
      immediate: true,
      deep: true,
      handler (val) {
        this.localFields = [...val]
      }
    }
  },
  methods: {
    getFieldTypeName (field) {
      return (
        field.fieldType?.name ||
        field.field_type?.name ||
        field.fieldTypeName ||
        'text'
      )
    },

    isSelected (field) {
      if (!this.selectedField) return false

      if (
        this.selectedField.id &&
        field.id &&
        this.selectedField.id === field.id
      ) {
        return true
      }

      if (
        this.selectedField._tempId &&
        field._tempId &&
        this.selectedField._tempId === field._tempId
      ) {
        return true
      }

      return false
    },

    isStandard (field) {
      return (
        field._isStandard === true ||
        (field.field_type && field.field_type.is_standard === true) ||
        (field.fieldType && field.fieldType.is_standard === true)
      )
    },

    isProtectedField (field) {
      const typeName = this.getFieldTypeName(field)
      return ['name', 'email'].includes(typeName)
    },

    isLockedNameField (field) {
      const typeName = this.getFieldTypeName(field)
      return ['name', 'email', 'phone'].includes(typeName)
    },

    isTextType (field) {
      const textTypes = [
        'text',
        'email',
        'phone',
        'cpf',
        'cnpj',
        'cpf_cnpj',
        'cep',
        'rg',
        'number',
        'password',
        'hidden',
        'url'
      ]
      return textTypes.includes(this.getFieldTypeName(field))
    },

    isSelectType (field) {
      const selectTypes = ['select', 'multiselect', 'state', 'city']
      return selectTypes.includes(this.getFieldTypeName(field))
    },

    isDateType (field) {
      const dateTypes = ['date', 'time', 'datetime', 'daterange', 'birthdate']
      return dateTypes.includes(this.getFieldTypeName(field))
    },

    hasConditions (field) {
      return field.conditions && field.conditions.length > 0
    },

    getConditionSummary (field) {
      if (!field.conditions || field.conditions.length === 0) return ''

      const firstCondition = field.conditions[0]

      if (firstCondition.rules && firstCondition.rules.length > 0) {
        const firstRule = firstCondition.rules[0]
        if (firstRule.source_field_id && firstRule.value) {
          const referencedField = this.fields.find(
            f =>
              (f.id && String(f.id) === String(firstRule.source_field_id)) ||
              (f._tempId &&
                String(f._tempId) === String(firstRule.source_field_id))
          )
          const fieldLabel = referencedField ? referencedField.label : 'Campo'
          return `${fieldLabel} = ${firstRule.value}`
        }
        return `${firstCondition.rules.length} condição(ões)`
      }

      return 'Tem condições'
    },

    getOptionColor (index) {
      return this.optionColors[index % this.optionColors.length]
    },

    selectField (field) {
      this.$emit('select-field', field)
    },

    removeField (field) {
      this.$emit('remove-field', field.id || field._tempId)
    },

    duplicateField (field) {
      this.$emit('duplicate-field', field.id || field._tempId)
    },

    onReorder () {
      this.$emit('reorder-fields', this.localFields)
    },

    onDrop (event) {
      const data = event.dataTransfer.getData('application/json')
      if (data) {
        try {
          const fieldType = JSON.parse(data)
          this.$emit('add-field', fieldType)
        } catch (e) {
          console.error('Invalid drop data', e)
        }
      }
    }
  }
}
</script>

<style scoped>
.form-canvas {
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 350px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 32px;
  color: #556ee6;
}

.empty-canvas h5 {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.empty-canvas p {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

/* Field Card */
.field-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.field-card:hover {
  border-color: #d0d5dd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.field-card.selected {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
}

.field-card.has-conditions {
  border-left: 3px solid #ffc107;
}

/* Field Header */
.field-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  padding: 4px;
}

.drag-handle:hover {
  color: #556ee6;
}

.drag-handle:active {
  cursor: grabbing;
}

.field-label-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-label {
  font-weight: 500;
  font-size: 14px;
  color: #344054;
}

.badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}

.badge-required {
  background: #fee2e2;
  color: #dc2626;
}

.badge-conditions {
  background: #fef3c7;
  color: #d97706;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}

.field-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.field-card:hover .field-actions {
  opacity: 1;
}

.btn-action {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-action:hover {
  background: #f3f4f6;
}

.btn-action.btn-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

.protected-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #059669;
  font-size: 16px;
}

/* Field Preview */
.field-preview {
  margin-bottom: 8px;
}

.preview-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
  resize: none;
}

.preview-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-options {
  margin-top: 8px;
  padding-left: 8px;
}

.preview-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #6b7280;
}

.option-bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.preview-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.radio-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
}

.preview-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.checkbox-box {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
}

.preview-date {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 2px dashed #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #9ca3af;
}

.preview-file i {
  font-size: 24px;
  margin-bottom: 8px;
}

.preview-heading {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.preview-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 8px 0;
}

/* Help Text */
.field-help {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* Conditions Indicator */
.conditions-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #fffbeb;
  border-radius: 4px;
  font-size: 12px;
  color: #92400e;
}

.conditions-indicator i {
  font-size: 14px;
}

/* Ghost */
.ghost-field {
  opacity: 0.5;
  background: #f3f4f6;
}
</style>
